import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  useNotification,
  getDialogHeading,
  dropDownValidation,
  renderSaveCancelButtons,
} from 'utils/commonUtils';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import TutorDialogCard from './TutorDialogCard';
import Typography from '@mui/material/Typography';
import { yupResolver } from '@hookform/resolvers/yup';
import DialogContent from '@mui/material/DialogContent';
import {
  removeAssignedTutor,
  getTutoringSubjectsV2Tutors,
  postTutoringSubjectsV2Tutor,
} from 'layouts/tutoringV2/services/tutoringAndSubjectsV2Api';
import { TutorsDialogLoader } from '../../tutoringV2CommonUtils';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { DialogAutoHeightStyle, manageTutorCategoryOptions } from 'constants';

const subjectsAndTurorsSchema = yup.object().shape({
  tutor: dropDownValidation,
  category: dropDownValidation,
});

const ManageTutorsDialog = (props) => {
  const { open, setOpen, selectedSubject, onTutorsUpdated } = props;
  const { subjectId } = selectedSubject;

  const { handleErrorResponse, NotificationPopup } = useNotification();

  const availableUsersList =
    useSelector((state) => state?.availableUsersList) || [];

  const [tutorsList, setTutorsList] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [tutorsLoading, settutorsLoading] = useState(false);
  const [removingTutorId, setRemovingTutorId] = useState(null);

  const availableUsersData = availableUsersList?.map((item) => ({
    id: item?.userId,
    value: `${item?.givenName} ${item?.familyName} (${item?.username})`,
  }));

  const defaultValue = {
    tutor: availableUsersData?.[0]?.id || '',
    category: manageTutorCategoryOptions?.[0]?.id || '',
  };

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(subjectsAndTurorsSchema),
  });

  const getTutoringSubjectsV2TutorsList = async () => {
    try {
      settutorsLoading(true);
      const list = await getTutoringSubjectsV2Tutors(subjectId);
      setTutorsList(list || []);
      settutorsLoading(false);
    } catch (error) {
      settutorsLoading(false);
    }
  };
  const { handleSubmit, reset, control, formState } = useFunction;
  const { errors } = formState;

  useEffect(() => {
    reset(defaultValue);
  }, [defaultValue.tutor, defaultValue.category]);

  useEffect(() => {
    getTutoringSubjectsV2TutorsList();
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = async (data) => {
    const { category, tutor } = data;
    const payload = { category, tutorId: tutor };

    try {
      setLoadingButton(true);
      await postTutoringSubjectsV2Tutor(subjectId, payload);
      const list = await getTutoringSubjectsV2Tutors(subjectId);
      setTutorsList(list || []);
      setLoadingButton(false);

      if (onTutorsUpdated) {
        onTutorsUpdated();
      }
    } catch (error) {
      setLoadingButton(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const getTutorName = (tutorId) => {
    const user = availableUsersList?.find((u) => u.userId === tutorId);
    return user ? `${user.givenName} ${user.familyName}` : tutorId;
  };

  const handleSwitchCategory = (tutor) => {
    console.log('Switch category clicked:', tutor);
  };

  const handleRemoveTutor = async (tutor) => {
    const { assignmentId } = tutor;

    try {
      setRemovingTutorId(assignmentId);
      await removeAssignedTutor(assignmentId);

      const updatedList = await getTutoringSubjectsV2Tutors(subjectId);
      setTutorsList(updatedList || []);

      if (onTutorsUpdated) {
        onTutorsUpdated();
      }
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    } finally {
      setRemovingTutorId(null);
    }
  };

  const showTutors = !tutorsLoading && tutorsList?.length > 0;

  return (
    <Dialog open={open} PaperProps={DialogAutoHeightStyle}>
      {getDialogHeading('Manage Tutors', handleClose)}
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <FormInputDropdown
              type="text"
              name="tutor"
              control={control}
              label="Tutor Name"
              errors={!!errors.tutor}
              options={availableUsersData}
              helperText={errors?.tutor?.message}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormInputDropdown
              type="text"
              name="category"
              label="Category"
              control={control}
              errors={!!errors.category}
              options={manageTutorCategoryOptions}
              helperText={errors?.category?.message}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            {renderSaveCancelButtons(
              handleClose,
              handleSubmit(onSubmit),
              loadingButton,
            )}
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={2}>
          {tutorsLoading && <TutorsDialogLoader />}

          {!tutorsLoading && tutorsList?.length === 0 && (
            <Grid item xs={12}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 5 }}
              >
                No tutors assigned to this subject.
              </Typography>
            </Grid>
          )}

          {showTutors &&
            tutorsList?.map((tutor) => {
              const isLoading = removingTutorId === tutor?.assignmentId;
              return (
                <TutorDialogCard
                  key={tutor.assignmentId}
                  tutor={tutor}
                  loading={isLoading}
                  getTutorName={getTutorName}
                  onRemoveTutor={handleRemoveTutor}
                  onSwitchCategory={handleSwitchCategory}
                />
              );
            })}
        </Grid>
      </DialogContent>
      <NotificationPopup />
    </Dialog>
  );
};

export default ManageTutorsDialog;
